<template>
    <div class="schedule-items">
        <label class="block text-sm font-medium mb-2">
            {{ lang('congresses::congresses.schedule.schedule_items') }}
        </label>

        <!-- Items List -->
        <Sortable v-if="items.length > 0" :list="items" item-key="_id" tag="div" class="space-y-2" @end="onReorder">
            <!-- eslint-disable -->
            <template #item="{ element, index }">
                <div class="flex items-center space-x-2">
                    <icon icon="hero/solid/ellipsis-vertical" class="size-4 text-gray-400 cursor-grab"></icon>
                    <AdminText
                        :model-value="element.value"
                        class="flex-1"
                        :placeholder="lang('congresses::congresses.schedule.schedule_item_placeholder')"
                        @update:model-value="(value) => updateItem(index, value)"
                    />
                    <Button type="button" size="sm" color="brand" variant="inline" @click="removeItem(index)">
                        <span class="action-icon">
                            <Icon icon="phosphor/regular/minus"></Icon>
                        </span>
                    </Button>
                </div>
            </template>
            <!-- eslint-enable -->
        </Sortable>

        <!-- Empty State -->
        <div v-else class="congress-schedule-empty-state">
            {{ lang('congresses::congresses.schedule.no_schedule_items') }}
        </div>

        <!-- Add Item Button -->
        <div class="mt-2">
            <Button type="button" size="sm" color="brand" variant="inline" @click="addItem">
                <span class="action-icon">
                    <Icon icon="phosphor/regular/list-plus"></Icon>
                </span>
                <span class="action-label-text">
                    {{ lang('congresses::congresses.schedule.add_schedule_item') }}
                </span>
            </Button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { lang } from '@/lang/composable';
import Sortable from '@src/Common/Components/Sortable.vue';
import Button from '@src/Common/Components/Button.vue';
import AdminText from '@src/Fields/Pages/Fields/AdminText.vue';

interface ScheduleItem {
    _id: string;
    value: string;
}

const modelValue = defineModel<string[]>({ default: () => [] });

// Internal state with objects for Sortable tracking
const items = ref<ScheduleItem[]>([]);

// Convert string[] to ScheduleItem[] on init and when modelValue changes externally
const syncFromModel = () => {
    const modelStrings = modelValue.value || [];
    // Only rebuild if arrays are different (avoid infinite loop)
    const currentStrings = items.value.map((i) => i.value);
    if (JSON.stringify(modelStrings) !== JSON.stringify(currentStrings)) {
        items.value = modelStrings.map((str) => ({
            _id: uuidv4(),
            value: str,
        }));
    }
};

// Initialize
syncFromModel();

// Watch for external changes to modelValue
watch(
    () => modelValue.value,
    () => syncFromModel(),
    { deep: true },
);

// Sync items back to modelValue
const syncToModel = () => {
    modelValue.value = items.value.map((i) => i.value);
};

const addItem = () => {
    items.value = [
        ...items.value,
        {
            _id: uuidv4(),
            value: '',
        },
    ];
    syncToModel();
};

const updateItem = (index: number, value: string) => {
    items.value[index].value = value;
    syncToModel();
};

const removeItem = (index: number) => {
    items.value = items.value.filter((_, idx) => idx !== index);
    syncToModel();
};

const onReorder = (event: { oldIndex: number; newIndex: number }) => {
    const reordered = [...items.value];
    const [movedItem] = reordered.splice(event.oldIndex, 1);
    reordered.splice(event.newIndex, 0, movedItem);
    items.value = reordered;
    syncToModel();
};
</script>
